import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useUpdateDoctor, useDoctor, useRoles, useAllDiagnosticRooms, useGenerateResetLink } from '../../services/httpServices/queries';
import { useDepartments } from '../../services/httpServices/queries/useDepartments';
import { useActiveDesignations } from '../../services/httpServices/queries/useDesignations';
import { useActiveSpecializations } from '../../services/httpServices/queries/useSpecializations';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';
import { TimePicker } from '../../components/ui/DatePicker';
import { mediaService } from '../../services/httpServices/mediaService';
import { ASSIGNABLE_SYSTEM_ROLES } from '../../hooks/usePermissions';
import { CopyResetLinkModal } from '../../components/ui/CopyResetLinkModal';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Plus,
  X,
  Minus,
  Upload,
  ImageIcon,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Stethoscope,
  Calendar,
  Save,
  Banknote,
  Shield,
  Copy,
  Globe,
  Briefcase,
  Building2,
  Phone,
  LayoutDashboard,
} from 'lucide-react';
import { FormField, FormSection } from '../../components/ui/form-section';

// SUPER_ADMIN is excluded — it is reserved for the master developer and is
// managed from the Developer Console (/settings/developer).
const ALL_SYSTEM_ROLES: string[] = ASSIGNABLE_SYSTEM_ROLES;

function getRoleLabel(role: string) {
  return role
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function DoctorEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // When the form was opened from the Users screen, return there instead of the
  // doctor detail page.
  const from = (location.state as { from?: string } | null)?.from;
  const backTo = from || `/doctors/${id}`;
  const backLabel = from ? 'Back to Users' : 'Back to Doctor';
  const updateDoctor = useUpdateDoctor();
  const generateResetLink = useGenerateResetLink();
  const { data: doctorData, isLoading: doctorLoading, error: doctorError } = useDoctor(id || '');
  const { data: departmentsData, isLoading: departmentsLoading } = useDepartments();
  const { data: designationsData, isLoading: designationsLoading } = useActiveDesignations();
  const { data: specializationsData, isLoading: specializationsLoading } = useActiveSpecializations();
  const { data: roomsData, isLoading: roomsLoading } = useAllDiagnosticRooms();
  const { data: rolesData } = useRoles();

  const doctor = doctorData?.data;

  const departmentOptions = (departmentsData?.data || []).map((d: any) => ({
    value: d.id,
    label: `${d.name} (${d.code})`,
  }));

  const designationOptions = (designationsData?.data || []).map((d: any) => ({
    value: d.id,
    label: d.name,
  }));

  const specializationOptions = (specializationsData?.data || []).map((s: any) => ({
    value: s.id,
    label: s.label,
  }));

  const roomOptions = (roomsData?.data || []).map((r: any) => ({
    value: r.id,
    label: r.name,
  }));

  const [formData, setFormData] = useState({
    name: '',
    employeeCode: '',
    qualifications: [''] as string[],
    registrationNumber: '',
    email: '',
    phone: '',
    departmentIds: [] as string[],
    designationId: '',
    specializationIds: [] as string[],
    consultationFeeNew: 0,
    consultationFeeFollowup: 0,
    consultationFeeReport: 0,
    photoUrl: '',
    isActive: true,
    dashboardAccess: false,
    showOnWebsite: false,
    roles: [] as string[],
    customRoleIds: [] as string[],
    consultationRoomId: '',
    consultationAvailability: [] as Array<{
      day: string;
      timeRanges: Array<{ title: string; from: string; to: string }>;
    }>,
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isPhotoDragOver, setIsPhotoDragOver] = useState(false);
  const [showResetLinkModal, setShowResetLinkModal] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (doctor) {
      setFormData({
        name: doctor.name || '',
        employeeCode: doctor.employeeCode || '',
        qualifications: doctor.qualifications?.length ? doctor.qualifications : [''],
        registrationNumber: doctor.registrationNumber || '',
        email: doctor.email || '',
        phone: doctor.phone || '',
        departmentIds: doctor.departments?.length
          ? doctor.departments.map((d) => d.id)
          : (doctor.departmentId ? [doctor.departmentId] : []),
        designationId: doctor.designationId || '',
        specializationIds: doctor.specializationIds || doctor.specializations?.map((s) => s.id) || [],
        consultationFeeNew: Number(doctor.consultationFeeNew) || 0,
        consultationFeeFollowup: Number(doctor.consultationFeeFollowup) || 0,
        consultationFeeReport: Number(doctor.consultationFeeReport) || 0,
        photoUrl: doctor.photoUrl || '',
        isActive: doctor.isActive ?? true,
        dashboardAccess: doctor.user?.dashboardAccess === true,
        showOnWebsite: doctor.showOnWebsite ?? false,
        roles: doctor.user?.roles || [],
        customRoleIds: doctor.user?.customRoles?.map((r: any) => r.id) || [],
        consultationRoomId: doctor.consultationRoomId || '',
        consultationAvailability: (doctor.consultationAvailability ?? [])
          .filter((a) => a && typeof a.day === 'string' && a.day.trim() !== '')
          .map((a) => ({
            day: a.day,
            timeRanges: a.timeRanges?.length ? a.timeRanges : [{ title: '', from: '', to: '' }],
          })),
      });
      setPhotoPreview(doctor.photoUrl || null);
    }
  }, [doctor]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Doctor name is required';
    if (!formData.employeeCode.trim()) errors.employeeCode = 'Doctor code is required';
    if (!formData.qualifications.some((q) => q.trim() !== '')) {
      errors.qualifications = 'At least one qualification is required';
    }
    if (!formData.specializationIds || formData.specializationIds.length === 0) errors.specializationIds = 'At least one specialization is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email address';
    }
    (['consultationFeeNew', 'consultationFeeFollowup', 'consultationFeeReport'] as const).forEach(
      (field) => {
        const num = formData[field];
        if (!Number.isFinite(num) || num < 0) errors[field] = 'Must be 0 or greater';
      },
    );
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !id) return;

    const data: any = {
      ...formData,
      qualifications: formData.qualifications.filter((q) => q.trim() !== ''),
      consultationAvailability: formData.consultationAvailability
        .filter((a) => (a.day ?? '').trim() !== '')
        .map((a) => ({
          day: a.day,
          timeRanges: (a.timeRanges ?? []).filter(
            (r) => (r.from ?? '').trim() !== '' && (r.to ?? '').trim() !== '',
          ),
        }))
        .filter((a) => a.timeRanges.length > 0),
    };
    if (!data.consultationRoomId) delete data.consultationRoomId;
    if (!data.employeeCode) delete data.employeeCode;
    if (!data.designationId) data.designationId = null;
    try {
      setIsUploading(true);
      if (photoFile) {
        data.photoUrl = (await mediaService.uploadImage(photoFile, 'doctor')).data.url;
      }
      await updateDoctor.mutateAsync({ id, data });
      navigate(backTo);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Something went wrong';
      setFormErrors({ general: message });
    } finally {
      setIsUploading(false);
    }
  };

  const updateField = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleCopyResetLink = async (): Promise<string | null> => {
    if (!doctor?.user?.id) return null;
    try {
      const res = await generateResetLink.mutateAsync(doctor.user.id);
      const url = res?.data?.url;
      if (!url) throw new Error('No reset link returned');
      return url;
    } catch {
      toast.error('Failed to generate reset link');
      return null;
    }
  };

  const addQualification = () => {
    setFormData((prev) => ({ ...prev, qualifications: [...prev.qualifications, ''] }));
  };

  const removeQualification = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      qualifications: prev.qualifications.filter((_, i) => i !== index),
    }));
  };

  const updateQualification = (index: number, value: string) => {
    setFormData((prev) => {
      const next = [...prev.qualifications];
      next[index] = value;
      return { ...prev, qualifications: next };
    });
  };

  // Consultation Availability handlers
  const addAvailabilityDay = () => {
    setFormData((prev) => ({
      ...prev,
      consultationAvailability: [
        ...prev.consultationAvailability,
        { day: '', timeRanges: [{ title: '', from: '', to: '' }] },
      ],
    }));
  };

  const removeAvailabilityDay = (dayIndex: number) => {
    setFormData((prev) => ({
      ...prev,
      consultationAvailability: prev.consultationAvailability.filter((_, i) => i !== dayIndex),
    }));
  };

  const updateAvailabilityDay = (dayIndex: number, day: string) => {
    setFormData((prev) => {
      const next = [...prev.consultationAvailability];
      next[dayIndex] = { ...next[dayIndex], day };
      return { ...prev, consultationAvailability: next };
    });
  };

  const addTimeRange = (dayIndex: number) => {
    setFormData((prev) => {
      const next = [...prev.consultationAvailability];
      next[dayIndex] = {
        ...next[dayIndex],
        timeRanges: [...next[dayIndex].timeRanges, { title: '', from: '', to: '' }],
      };
      return { ...prev, consultationAvailability: next };
    });
  };

  const removeTimeRange = (dayIndex: number, rangeIndex: number) => {
    setFormData((prev) => {
      const next = [...prev.consultationAvailability];
      next[dayIndex] = {
        ...next[dayIndex],
        timeRanges: next[dayIndex].timeRanges.filter((_, i) => i !== rangeIndex),
      };
      return { ...prev, consultationAvailability: next };
    });
  };

  const updateTimeRange = (dayIndex: number, rangeIndex: number, field: string, value: string) => {
    setFormData((prev) => {
      const next = [...prev.consultationAvailability];
      const ranges = [...next[dayIndex].timeRanges];
      ranges[rangeIndex] = { ...ranges[rangeIndex], [field]: value };
      next[dayIndex] = { ...next[dayIndex], timeRanges: ranges };
      return { ...prev, consultationAvailability: next };
    });
  };

  // Photo handlers
  const handlePhotoDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsPhotoDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handlePhotoFile(file);
    }
  }, []);

  const handlePhotoDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsPhotoDragOver(true);
  }, []);

  const handlePhotoDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsPhotoDragOver(false);
  }, []);

  const handlePhotoFile = (file: File) => {
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setPhotoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handlePhotoFile(file);
    }
  };

  const clearPhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    updateField('photoUrl', '');
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const inputBase =
    'w-full h-9 px-3 border rounded-lg bg-background text-text placeholder:text-text-muted/60 focus:outline-none focus:ring-2 text-sm transition-colors';
  const inputValid = 'border-border focus:ring-primary/20 focus:border-primary';
  const inputInvalid = 'border-danger focus:ring-danger/20 focus:border-danger';
  const labelClass =
    'mb-1.5 flex items-center gap-1 text-[0.6875rem] font-semibold uppercase tracking-wider text-text-muted';

  const ic = (field: string) => `${inputBase} ${formErrors[field] ? inputInvalid : inputValid}`;
  const err = (field: string) => formErrors[field] ? <p className="text-xs text-danger mt-1">{formErrors[field]}</p> : null;

  if (doctorLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (doctorError || !doctor) {
    return (
      <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
        <AlertCircle size={18} />
        <span>Failed to load doctor details. Please try again.</span>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4 pb-2">
      {/* ─── Page header ─── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(backTo)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-surface text-text-muted transition-colors hover:border-primary/40 hover:text-primary"
            title={backLabel}
          >
            <ArrowLeft size={17} />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="truncate text-xl font-bold leading-tight text-text">Edit Doctor</h1>
              <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wider text-primary">
                {formData.employeeCode || 'No code'}
              </span>
            </div>
            <p className="truncate text-xs text-text-muted">{formData.name || 'Doctor profile'}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => updateField('isActive', !formData.isActive)}
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
            formData.isActive
              ? 'border-success/20 bg-success/10 text-success'
              : 'border-danger/20 bg-danger/10 text-danger'
          }`}
          title="Toggle account status"
        >
          <span className={`h-1.5 w-1.5 rounded-full ${formData.isActive ? 'bg-success' : 'bg-danger'}`} />
          {formData.isActive ? 'Active' : 'Inactive'}
        </button>
      </div>

      {/* General error */}
      {formErrors.general && (
        <div className="flex items-center gap-2 rounded-xl border border-danger/20 bg-danger/10 p-3 text-danger">
          <AlertCircle size={16} className="shrink-0" />
          <span className="text-xs font-medium">{formErrors.general}</span>
        </div>
      )}

      <form
        id="doctor-edit-form"
        onSubmit={handleSubmit}
        className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_340px]"
      >
        {/* ═══ Main column ═══ */}
        <div className="min-w-0 space-y-4">
          {/* ─── Basic information ─── */}
          <FormSection
            icon={<Stethoscope size={16} />}
            title="Basic Information"
            description="Name, code and consulting setup"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <FormField label="Full Name" required error={formErrors.name}>
                  <input
                    className={ic('name')}
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="Dr. John Doe"
                  />
                </FormField>
                <FormField label="Doctor Code" required error={formErrors.employeeCode}>
                  <input
                    className={`${ic('employeeCode')} font-mono`}
                    value={formData.employeeCode}
                    onChange={(e) => updateField('employeeCode', e.target.value)}
                    placeholder="DOC-2026-0001"
                    title="Auto-generated — you can edit it"
                  />
                </FormField>
                <FormField label="Designation">
                  <SearchableSelect
                    value={formData.designationId}
                    onChange={(v) => updateField('designationId', v)}
                    options={designationOptions}
                    placeholder="Select Designation (Optional)"
                    loading={designationsLoading}
                    error={err('designationId')}
                  />
                </FormField>
                <FormField label="Consultation Room">
                  <SearchableSelect
                    value={formData.consultationRoomId}
                    onChange={(v) => updateField('consultationRoomId', v)}
                    options={roomOptions}
                    placeholder="Select Room (Optional)"
                    loading={roomsLoading}
                  />
                </FormField>
              </div>
          </FormSection>

          {/* ─── Departments & specializations ─── */}
          <FormSection
            icon={<Building2 size={16} />}
            title="Departments & Specializations"
            description="Where the doctor practises"
          >
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {/* Departments */}
              <div>
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <label className={labelClass}>Departments</label>
                  {departmentsLoading && (
                    <Loader2 size={12} className="animate-spin text-text-muted" />
                  )}
                </div>
                <div className="mb-2 flex min-h-[26px] flex-wrap gap-1.5">
                  {formData.departmentIds.length === 0 ? (
                    <span className="text-[0.6875rem] italic text-text-muted">No departments selected</span>
                  ) : (
                    formData.departmentIds.map((id: string) => {
                      const dept = departmentOptions.find((d: any) => d.value === id);
                      if (!dept) return null;
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[0.6875rem] font-medium text-white shadow-sm"
                        >
                          {dept.label}
                          <button
                            type="button"
                            onClick={() =>
                              updateField(
                                'departmentIds',
                                formData.departmentIds.filter((did) => did !== id),
                              )
                            }
                            className="rounded-full p-0.5 transition-colors hover:bg-white/20"
                            title={`Remove ${dept.label}`}
                          >
                            <X size={10} strokeWidth={3} />
                          </button>
                        </span>
                      );
                    })
                  )}
                </div>
                {departmentOptions.length > 0 && (
                  <div className="max-h-36 overflow-y-auto rounded-lg border border-border bg-background/50 p-2.5">
                    <p className="mb-2 text-[0.625rem] font-semibold uppercase tracking-wider text-text-muted">
                      Add departments
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {departmentOptions.map((dept: any) => {
                        if (formData.departmentIds.includes(dept.value)) return null;
                        return (
                          <button
                            key={dept.value}
                            type="button"
                            onClick={() =>
                              updateField('departmentIds', [...formData.departmentIds, dept.value])
                            }
                            className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-2.5 py-1 text-[0.6875rem] font-medium text-text transition-all hover:border-primary hover:bg-primary/5 hover:text-primary"
                          >
                            <Plus size={10} />
                            {dept.label}
                          </button>
                        );
                      })}
                      {departmentOptions.every((d: any) =>
                        formData.departmentIds.includes(d.value),
                      ) && (
                        <span className="text-[0.6875rem] italic text-text-muted">
                          All departments selected
                        </span>
                      )}
                    </div>
                  </div>
                )}
                {departmentOptions.length === 0 && !departmentsLoading && (
                  <span className="text-[0.6875rem] text-text-muted">No departments available.</span>
                )}
                {departmentOptions.length === 0 && departmentsLoading && (
                  <span className="flex items-center gap-1 text-[0.6875rem] text-text-muted">
                    <Loader2 size={12} className="animate-spin" /> Loading departments...
                  </span>
                )}
                {err('departmentIds')}
              </div>

              {/* Specializations */}
              <div>
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <label className={labelClass}>
                    Specializations <span className="text-danger">*</span>
                  </label>
                  {specializationsLoading && (
                    <Loader2 size={12} className="animate-spin text-text-muted" />
                  )}
                </div>
                <div className="mb-2 flex min-h-[26px] flex-wrap gap-1.5">
                  {formData.specializationIds.length === 0 ? (
                    <span className="text-[0.6875rem] italic text-text-muted">
                      No specializations selected
                    </span>
                  ) : (
                    formData.specializationIds.map((id: string) => {
                      const spec = specializationOptions.find((s: any) => s.value === id);
                      if (!spec) return null;
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[0.6875rem] font-medium text-white shadow-sm"
                        >
                          {spec.label}
                          <button
                            type="button"
                            onClick={() =>
                              updateField(
                                'specializationIds',
                                formData.specializationIds.filter((sid) => sid !== id),
                              )
                            }
                            className="rounded-full p-0.5 transition-colors hover:bg-white/20"
                            title={`Remove ${spec.label}`}
                          >
                            <X size={10} strokeWidth={3} />
                          </button>
                        </span>
                      );
                    })
                  )}
                </div>
                {specializationOptions.length > 0 && (
                  <div className="max-h-36 overflow-y-auto rounded-lg border border-border bg-background/50 p-2.5">
                    <p className="mb-2 text-[0.625rem] font-semibold uppercase tracking-wider text-text-muted">
                      Add specializations
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {specializationOptions.map((spec: any) => {
                        if (formData.specializationIds.includes(spec.value)) return null;
                        return (
                          <button
                            key={spec.value}
                            type="button"
                            onClick={() =>
                              updateField('specializationIds', [
                                ...formData.specializationIds,
                                spec.value,
                              ])
                            }
                            className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-2.5 py-1 text-[0.6875rem] font-medium text-text transition-all hover:border-primary hover:bg-primary/5 hover:text-primary"
                          >
                            <Plus size={10} />
                            {spec.label}
                          </button>
                        );
                      })}
                      {specializationOptions.every((s: any) =>
                        formData.specializationIds.includes(s.value),
                      ) && (
                        <span className="text-[0.6875rem] italic text-text-muted">
                          All specializations selected
                        </span>
                      )}
                    </div>
                  </div>
                )}
                {specializationOptions.length === 0 && !specializationsLoading && (
                  <span className="text-[0.6875rem] text-text-muted">No specializations available.</span>
                )}
                {specializationOptions.length === 0 && specializationsLoading && (
                  <span className="flex items-center gap-1 text-[0.6875rem] text-text-muted">
                    <Loader2 size={12} className="animate-spin" /> Loading specializations...
                  </span>
                )}
                {err('specializationIds')}
              </div>
            </div>
          </FormSection>

          {/* ─── Professional details ─── */}
          <FormSection
            icon={<Briefcase size={16} />}
            title="Professional Details"
            description="Qualifications and registration"
            action={
              <button
                type="button"
                onClick={addQualification}
                className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-primary/30 px-2.5 py-1 text-[0.6875rem] font-medium text-primary transition-colors hover:bg-primary/5"
              >
                <Plus size={12} /> Add Qualification
              </button>
            }
          >
            <div className="space-y-2">
              {formData.qualifications.map((qual, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    className={`${inputBase} ${
                      formErrors.qualifications ? inputInvalid : inputValid
                    } flex-1`}
                    value={qual}
                    onChange={(e) => updateQualification(index, e.target.value)}
                    placeholder={`Qualification ${index + 1} e.g. MBBS`}
                  />
                  {formData.qualifications.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeQualification(index)}
                      className="rounded-lg p-2 text-text-muted transition-colors hover:bg-danger/10 hover:text-danger"
                      title="Remove qualification"
                    >
                      <Minus size={15} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {err('qualifications')}
            <div className="mt-3 sm:max-w-xs">
              <FormField label="Registration Number" error={formErrors.registrationNumber}>
                <input
                  className={ic('registrationNumber')}
                  value={formData.registrationNumber}
                  onChange={(e) => updateField('registrationNumber', e.target.value)}
                  placeholder="BMDC-12345"
                />
              </FormField>
            </div>
          </FormSection>

          {/* ─── Consultation fees ─── */}
          <FormSection
            icon={<Banknote size={16} />}
            title="Consultation Fees"
            description="Applied automatically when an appointment is booked"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <FormField label="New Patient (৳)" error={formErrors.consultationFeeNew}>
                <ValidatedNumberInput
                  className={ic('consultationFeeNew')}
                  value={formData.consultationFeeNew}
                  onChange={(v) => updateField('consultationFeeNew', v)}
                  placeholder="0.00"
                />
              </FormField>
              <FormField label="Follow-up Patient (৳)" error={formErrors.consultationFeeFollowup}>
                <ValidatedNumberInput
                  className={ic('consultationFeeFollowup')}
                  value={formData.consultationFeeFollowup}
                  onChange={(v) => updateField('consultationFeeFollowup', v)}
                  placeholder="0.00"
                />
              </FormField>
              <FormField label="Report Check (৳)" error={formErrors.consultationFeeReport}>
                <ValidatedNumberInput
                  className={ic('consultationFeeReport')}
                  value={formData.consultationFeeReport}
                  onChange={(v) => updateField('consultationFeeReport', v)}
                  placeholder="0.00"
                />
              </FormField>
            </div>
          </FormSection>

          {/* ─── Contact information ─── */}
          <FormSection
            icon={<Phone size={16} />}
            title="Contact Information"
            description="How to reach the doctor"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField label="Email" error={formErrors.email}>
                <input
                  type="email"
                  className={ic('email')}
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  placeholder="doctor@hospital.com"
                />
              </FormField>
              <FormField label="Phone" error={formErrors.phone}>
                <input
                  type="tel"
                  className={ic('phone')}
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  placeholder="+8801234567890"
                />
              </FormField>
            </div>
          </FormSection>

          {/* ─── Consultation availability ─── */}
          <FormSection
            icon={<Calendar size={16} />}
            title="Consultation Availability"
            description="Weekdays and time slots when the doctor consults"
            action={
              <button
                type="button"
                onClick={addAvailabilityDay}
                className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-primary/30 px-2.5 py-1 text-[0.6875rem] font-medium text-primary transition-colors hover:bg-primary/5"
              >
                <Plus size={12} /> Add Day
              </button>
            }
          >
            {formData.consultationAvailability.length === 0 && (
              <div className="rounded-lg border border-dashed border-border bg-background p-4 text-center text-xs text-text-muted">
                No availability days added. Use “Add Day” to set the consultation schedule.
              </div>
            )}

            <div className="space-y-3">
              {formData.consultationAvailability.map((availability, dayIndex) => (
                <div
                  key={dayIndex}
                  className="space-y-3 rounded-xl border border-border bg-background/50 p-3"
                >
                  <div className="flex items-center gap-2">
                    <Calendar size={15} className="shrink-0 text-primary" />
                    <select
                      className={`${inputBase} ${inputValid}`}
                      value={availability.day}
                      onChange={(e) => updateAvailabilityDay(dayIndex, e.target.value)}
                    >
                      <option value="">Select Day</option>
                      <option value="Everyday">Everyday (All Days)</option>
                      <option value="Sunday">Sunday</option>
                      <option value="Monday">Monday</option>
                      <option value="Tuesday">Tuesday</option>
                      <option value="Wednesday">Wednesday</option>
                      <option value="Thursday">Thursday</option>
                      <option value="Friday">Friday</option>
                      <option value="Saturday">Saturday</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => removeAvailabilityDay(dayIndex)}
                      className="shrink-0 rounded-lg p-2 text-text-muted transition-colors hover:bg-danger/10 hover:text-danger"
                      title="Remove day"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>

                  <div className="space-y-2 sm:pl-6">
                    {availability.timeRanges.map((range, rangeIndex) => (
                      <div
                        key={rangeIndex}
                        className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[minmax(0,1fr)_8.5rem_8.5rem_auto]"
                      >
                        <input
                          className={`${inputBase} ${inputValid}`}
                          placeholder="Title e.g. Morning Session"
                          value={range.title}
                          onChange={(e) =>
                            updateTimeRange(dayIndex, rangeIndex, 'title', e.target.value)
                          }
                        />
                        <TimePicker
                          className={`${inputBase} ${inputValid}`}
                          value={range.from}
                          onChange={(value) =>
                            updateTimeRange(dayIndex, rangeIndex, 'from', value)
                          }
                        />
                        <TimePicker
                          className={`${inputBase} ${inputValid}`}
                          value={range.to}
                          onChange={(value) =>
                            updateTimeRange(dayIndex, rangeIndex, 'to', value)
                          }
                        />
                        {availability.timeRanges.length > 1 ? (
                          <button
                            type="button"
                            onClick={() => removeTimeRange(dayIndex, rangeIndex)}
                            className="rounded-lg p-2 text-text-muted transition-colors hover:bg-danger/10 hover:text-danger"
                            title="Remove time range"
                          >
                            <Minus size={15} />
                          </button>
                        ) : (
                          <span className="hidden w-8 sm:block" />
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addTimeRange(dayIndex)}
                      className="inline-flex items-center gap-1 rounded-lg border border-primary/30 px-2.5 py-1 text-[0.6875rem] font-medium text-primary transition-colors hover:bg-primary/5"
                    >
                      <Plus size={12} /> Add Time Range
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </FormSection>
        </div>

        {/* ═══ Sidebar ═══ */}
        <aside className="space-y-4">
          {/* Profile photo */}
          <FormSection
            icon={<ImageIcon size={16} />}
            title="Profile Photo"
            description="Shown across the doctor profile"
          >
            <div className="mx-auto w-[168px]">
              <label className={labelClass}>Photo</label>
              <div
                onDrop={handlePhotoDrop}
                onDragOver={handlePhotoDragOver}
                onDragLeave={handlePhotoDragLeave}
                onClick={() => photoInputRef.current?.click()}
                className={`relative flex h-[200px] w-full cursor-pointer flex-col items-center justify-center overflow-hidden rounded-xl border-2 border-dashed transition-colors ${
                  isPhotoDragOver
                    ? 'border-primary bg-primary/5'
                    : photoPreview
                      ? 'border-success bg-success/5'
                      : 'border-border bg-background hover:border-primary/50 hover:bg-primary/5'
                }`}
              >
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoSelect}
                />
                {photoPreview ? (
                  <>
                    <img
                      src={photoPreview}
                      alt="Doctor preview"
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearPhoto();
                      }}
                      className="absolute right-2 top-2 z-10 rounded-lg bg-danger p-1.5 text-white shadow-sm transition-colors hover:bg-danger-dark"
                      title="Remove photo"
                    >
                      <Trash2 size={14} />
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                      <p className="text-center text-xs font-medium text-white">
                        Click or drop to replace
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center gap-2 p-3 text-text-muted">
                    <div
                      className={`rounded-full p-3 transition-colors ${
                        isPhotoDragOver ? 'bg-primary/10' : 'bg-background'
                      }`}
                    >
                      {isPhotoDragOver ? (
                        <Upload size={24} className="text-primary" />
                      ) : (
                        <ImageIcon size={24} />
                      )}
                    </div>
                    <p className="text-center text-xs font-medium">
                      {isPhotoDragOver ? 'Drop image here' : 'Drag & drop or click'}
                    </p>
                    <p className="text-center text-[0.625rem] opacity-70">JPG, PNG, WEBP up to 5MB</p>
                  </div>
                )}
              </div>
            </div>
          </FormSection>

          {/* Visibility & status */}
          <FormSection
            icon={<Globe size={16} />}
            title="Visibility & Status"
            description="Where this doctor is available"
          >
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background/60 p-3">
                <div className="flex min-w-0 items-center gap-2.5">
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                      formData.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                    }`}
                  >
                    {formData.isActive ? <ToggleRight size={17} /> : <ToggleLeft size={17} />}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-text">
                      {formData.isActive ? 'Active' : 'Inactive'}
                    </p>
                    <p className="text-[0.6875rem] leading-snug text-text-muted">
                      {formData.isActive
                        ? 'Visible in booking and referrals.'
                        : 'Hidden from booking and referrals.'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.isActive}
                  onClick={() => updateField('isActive', !formData.isActive)}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                    formData.isActive ? 'bg-success' : 'bg-border'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                      formData.isActive ? 'translate-x-[24px]' : 'translate-x-[4px]'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background/60 p-3">
                <div className="flex min-w-0 items-center gap-2.5">
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                      formData.showOnWebsite
                        ? 'bg-primary/10 text-primary'
                        : 'bg-border/40 text-text-muted'
                    }`}
                  >
                    <Globe size={16} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-text">Website</p>
                    <p className="text-[0.6875rem] leading-snug text-text-muted">
                      {formData.showOnWebsite
                        ? 'Shown on the public website.'
                        : 'Hidden from the public website.'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.showOnWebsite}
                  onClick={() => updateField('showOnWebsite', !formData.showOnWebsite)}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                    formData.showOnWebsite ? 'bg-primary' : 'bg-border'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                      formData.showOnWebsite ? 'translate-x-[24px]' : 'translate-x-[4px]'
                    }`}
                  />
                </button>
              </div>
            </div>
          </FormSection>

          {/* Dashboard access */}
          <FormSection
            icon={<LayoutDashboard size={16} />}
            title="Dashboard Access"
            description="Allow this doctor to sign in"
          >
            <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background/60 p-3">
              <div className="flex min-w-0 items-center gap-2.5">
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                    formData.dashboardAccess
                      ? 'bg-success/10 text-success'
                      : 'bg-background text-text-muted'
                  }`}
                >
                  <LayoutDashboard size={17} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text">
                    {formData.dashboardAccess ? 'Access Allowed' : 'Access Denied'}
                  </p>
                  <p className="text-[0.6875rem] leading-snug text-text-muted">
                    {formData.dashboardAccess
                      ? 'The doctor can sign in to the dashboard.'
                      : 'The doctor cannot sign in.'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={formData.dashboardAccess}
                onClick={() => updateField('dashboardAccess', !formData.dashboardAccess)}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                  formData.dashboardAccess ? 'bg-success' : 'bg-border'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                    formData.dashboardAccess ? 'translate-x-[24px]' : 'translate-x-[4px]'
                  }`}
                />
              </button>
            </div>
            {!doctor.user && (
              <p className="mt-2 rounded-lg border border-info/20 bg-info/10 p-2 text-[0.6875rem] leading-relaxed text-info">
                No system user yet — one will be created and linked on save.
              </p>
            )}
          </FormSection>

          {/* System roles */}
          <FormSection
            icon={<Shield size={16} />}
            title="System Roles"
            description="Built-in dashboard access"
            action={
              doctor.user ? (
                <button
                  type="button"
                  onClick={() => setShowResetLinkModal(true)}
                  disabled={generateResetLink.isPending}
                  title="Generate the reset link and copy it to your clipboard (no email sent)"
                  className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-primary/30 px-2 py-1 text-[0.625rem] font-medium text-primary transition-colors hover:bg-primary/5 disabled:opacity-50"
                >
                  {generateResetLink.isPending ? (
                    <Loader2 size={11} className="animate-spin" />
                  ) : (
                    <Copy size={11} />
                  )}
                  Reset Link
                </button>
              ) : undefined
            }
          >
            {!doctor.user && (
              <p className="mb-2 rounded-lg border border-info/20 bg-info/10 p-2 text-[0.6875rem] leading-relaxed text-info">
                No system user yet — one will be created and linked on save.
              </p>
            )}
            <div className="flex min-h-[26px] flex-wrap gap-1.5">
              {formData.roles.length === 0 ? (
                <span className="text-[0.6875rem] italic text-text-muted">No system roles assigned</span>
              ) : (
                formData.roles.map((role) => (
                  <span
                    key={role}
                    className="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[0.6875rem] font-medium text-white shadow-sm"
                  >
                    {getRoleLabel(role)}
                    <button
                      type="button"
                      onClick={() => updateField('roles', formData.roles.filter((r) => r !== role))}
                      className="rounded-full p-0.5 transition-colors hover:bg-white/20"
                      title={`Remove ${getRoleLabel(role)}`}
                    >
                      <X size={10} strokeWidth={3} />
                    </button>
                  </span>
                ))
              )}
            </div>
            <div className="mt-2.5 max-h-44 overflow-y-auto rounded-lg border border-border bg-background/50 p-2.5">
              <p className="mb-2 text-[0.625rem] font-semibold uppercase tracking-wider text-text-muted">
                Add roles
              </p>
              <div className="flex flex-wrap gap-1.5">
                {ALL_SYSTEM_ROLES.map((role) => {
                  if (formData.roles.includes(role)) return null;
                  return (
                    <button
                      key={role}
                      type="button"
                      onClick={() => updateField('roles', [...formData.roles, role])}
                      className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-2.5 py-1 text-[0.6875rem] font-medium text-text transition-all hover:border-primary hover:bg-primary/5 hover:text-primary"
                    >
                      <Plus size={10} />
                      {getRoleLabel(role)}
                    </button>
                  );
                })}
                {ALL_SYSTEM_ROLES.every((r) => formData.roles.includes(r)) && (
                  <span className="text-[0.6875rem] italic text-text-muted">All system roles assigned</span>
                )}
              </div>
            </div>
          </FormSection>

          {/* Custom roles */}
          <FormSection
            icon={<Shield size={16} />}
            title="Custom Roles"
            description="Additional permission sets"
          >
            <div className="flex min-h-[26px] flex-wrap gap-1.5">
              {formData.customRoleIds.length === 0 ? (
                <span className="text-[0.6875rem] italic text-text-muted">No custom roles assigned</span>
              ) : (
                formData.customRoleIds.map((id: string) => {
                  const role = (rolesData?.data || []).find((r: any) => r.id === id);
                  if (!role) return null;
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[0.6875rem] font-medium text-white shadow-sm"
                    >
                      {role.name}
                      <button
                        type="button"
                        onClick={() =>
                          updateField(
                            'customRoleIds',
                            formData.customRoleIds.filter((rid) => rid !== id),
                          )
                        }
                        className="rounded-full p-0.5 transition-colors hover:bg-white/20"
                        title={`Remove ${role.name}`}
                      >
                        <X size={10} strokeWidth={3} />
                      </button>
                    </span>
                  );
                })
              )}
            </div>
            {(rolesData?.data || []).length > 0 ? (
              <div className="mt-2.5 max-h-44 overflow-y-auto rounded-lg border border-border bg-background/50 p-2.5">
                <p className="mb-2 text-[0.625rem] font-semibold uppercase tracking-wider text-text-muted">
                  Add roles
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {(rolesData?.data || []).map((role: any) => {
                    if (formData.customRoleIds.includes(role.id)) return null;
                    return (
                      <button
                        key={role.id}
                        type="button"
                        onClick={() =>
                          updateField('customRoleIds', [...formData.customRoleIds, role.id])
                        }
                        className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-2.5 py-1 text-[0.6875rem] font-medium text-text transition-all hover:border-primary hover:bg-primary/5 hover:text-primary"
                      >
                        <Plus size={10} />
                        {role.name}
                      </button>
                    );
                  })}
                  {(rolesData?.data || []).every((r: any) =>
                    formData.customRoleIds.includes(r.id),
                  ) && (
                    <span className="text-[0.6875rem] italic text-text-muted">All roles assigned</span>
                  )}
                </div>
              </div>
            ) : (
              <p className="mt-2 text-[0.6875rem] text-text-muted">
                No roles available. Create one in Users {'>'} Roles first.
              </p>
            )}
          </FormSection>
        </aside>

        {/* ─── Sticky footer actions ─── */}
        <div className="sticky bottom-0 z-20 flex items-center justify-between gap-3 rounded-xl border border-border bg-surface/95 px-3 py-2.5 shadow-lg backdrop-blur xl:col-span-2">
          <p className="hidden text-xs text-text-muted sm:block">
            Fields marked <span className="font-semibold text-danger">*</span> are required.
          </p>
          <div className="flex w-full items-center justify-end gap-2 sm:w-auto">
            <button
              type="button"
              onClick={() => navigate(backTo)}
              className="h-9 rounded-lg border border-border bg-surface px-3.5 text-sm font-medium text-text-muted transition-colors hover:bg-background hover:text-text"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateDoctor.isPending || isUploading}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-white transition-colors hover:bg-primary-dark disabled:opacity-50"
            >
              {(updateDoctor.isPending || isUploading) && (
                <Loader2 size={15} className="animate-spin" />
              )}
              <Save size={15} />
              {isUploading ? 'Uploading…' : updateDoctor.isPending ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </form>

      {/* Copy Reset Link Confirmation */}
      <CopyResetLinkModal
        open={showResetLinkModal}
        userName={formData.name || doctor.name}
        userEmail={doctor.user?.email}
        onConfirm={handleCopyResetLink}
        onClose={() => setShowResetLinkModal(false)}
      />
    </div>
  );
}
